import { Link } from 'react-router-dom';
import { Menu, X, RotateCcw, Search, Bookmark, Home } from 'lucide-react';
import { Progress } from '../components/ui/progress';
import { Button } from '../components/ui/button';
import { useCourse } from '../context/CourseContext';
import { useTheme } from '../context/ThemeContext';
import { useState } from 'react';
import SearchDialog from './SearchDialog';
import BookmarksPanel from './BookmarksPanel';

const STEADFAST_LOGO = "https://customer-assets.emergentagent.com/job_sfdc-training-ebook/artifacts/5eqwermj_logo.png";
const SALESFORCE_LOGO = "https://customer-assets.emergentagent.com/job_sfdc-training-ebook/artifacts/nr61ks3q_Salesforce.com_logo.png";

export default function Header({ onToggleSidebar, sidebarOpen, courseSections }) {
  const { getOverallProgress, resetProgress, bookmarks } = useCourse();
  const { theme, toggleTheme } = useTheme();
  const [showReset, setShowReset] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [bookmarksOpen, setBookmarksOpen] = useState(false);
  const progress = getOverallProgress();

  return (
    <>
      <header
        data-testid="main-header"
        className="sticky top-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border/50"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Left: Steadfast Logo */}
            <div className="flex items-center gap-2">
              {onToggleSidebar && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="lg:hidden"
                  onClick={onToggleSidebar}
                  data-testid="toggle-sidebar-btn"
                >
                  {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </Button>
              )}
              <Link to="/home" className="flex items-center gap-2 hover:opacity-80 transition-opacity" data-testid="header-logo-link">
                <img
                  src={STEADFAST_LOGO}
                  alt="Steadfast"
                  className="h-10 sm:h-12 w-auto object-contain"
                  data-testid="steadfast-logo"
                />
              </Link>
            </div>

            {/* Center: Controls */}
            <div className="flex items-center gap-1.5 sm:gap-2">
              {/* Home */}
              <Link to="/home">
                <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-foreground" data-testid="home-btn">
                  <Home className="h-4 w-4" />
                </Button>
              </Link>

              {/* Search */}
              <Button
                variant="outline"
                size="sm"
                className="h-8 gap-2 text-muted-foreground hover:text-foreground hidden sm:flex"
                onClick={() => setSearchOpen(true)}
                data-testid="search-btn"
              >
                <Search className="h-3.5 w-3.5" />
                <span className="text-xs">Search</span>
                <kbd className="text-[10px] bg-muted px-1 py-0.5 rounded border border-border ml-1">Ctrl+K</kbd>
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8 sm:hidden" onClick={() => setSearchOpen(true)} data-testid="search-btn-mobile">
                <Search className="h-4 w-4" />
              </Button>



              {/* Bookmarks */}
              <div className="relative">
                <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-sf-warning" onClick={() => setBookmarksOpen(true)} data-testid="bookmarks-btn">
                  <Bookmark className={`h-4 w-4 ${bookmarks.length > 0 ? 'text-sf-warning fill-sf-warning' : ''}`} />
                </Button>
                {bookmarks.length > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-sf-warning text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                    {bookmarks.length > 9 ? '9+' : bookmarks.length}
                  </span>
                )}
              </div>

              {/* Progress */}
              <div className="hidden sm:flex items-center gap-2 min-w-[120px]">
                <Progress value={progress} className="h-2 flex-1" data-testid="overall-progress-bar" />
                <span className="text-xs font-semibold text-primary tabular-nums" data-testid="overall-progress-text">{progress}%</span>
              </div>

              {/* Reset */}
              <div className="relative">
                <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-foreground" onClick={() => setShowReset(!showReset)} data-testid="reset-menu-btn">
                  <RotateCcw className="h-4 w-4" />
                </Button>
                {showReset && (
                  <div className="absolute right-0 top-full mt-2 bg-card border border-border rounded-lg shadow-lg p-3 z-50 w-48">
                    <p className="text-xs text-muted-foreground mb-2">Reset all progress?</p>
                    <div className="flex gap-2">
                      <Button size="sm" variant="destructive" className="text-xs flex-1" onClick={() => { resetProgress(); setShowReset(false); }} data-testid="confirm-reset-btn">Reset</Button>
                      <Button size="sm" variant="outline" className="text-xs flex-1" onClick={() => setShowReset(false)} data-testid="cancel-reset-btn">Cancel</Button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right: Salesforce Logo */}
            <div className="flex items-center">
              <img
                src={SALESFORCE_LOGO}
                alt="Salesforce"
                className="h-10 sm:h-12 w-auto object-contain"
                data-testid="salesforce-logo"
              />
            </div>
          </div>
        </div>
      </header>
      <SearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
      <BookmarksPanel open={bookmarksOpen} onOpenChange={setBookmarksOpen} sections={courseSections} />
    </>
  );
}
